import cn from "classnames";
import { observer } from "mobx-react";
import { useEffect, useState, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";

import pokerIcon from "src/assets/icons/cardsIcon.svg";
import dicechessIcon from "src/assets/icons/diceChessIcon.svg";
import dealerIcon from "src/assets/icons/rouletteIcon.svg";
import slotIcon from "src/assets/icons/slotsIcon.svg";
import sportIcon from "src/assets/icons/sportIcon.svg";
import virtualSportsIcon from "src/assets/icons/virtualSports.png";
import tournamentIcon from "src/assets/icons/tournamentTrophy.svg";
import cashBattleicon from "src/assets/icons/CoinsCB.svg";
import freePlayIcon from "src/assets/icons/HandCoinsFP.svg";
import rulesIcon from "src/assets/icons/rulesIcon.svg";

import paths from "src/consts/paths";
import useStores from "src/hooks/useStores";
import { useLocale } from "src/providers/LocaleProvider";
import { GameTypes } from "src/store/models";

import { NavItem } from "./components/NavItem";
import { ToggleIcon } from "./components/ToggleIcon";
import { useStyles } from "./styles";

export const SideBar = observer(() => {
    const styles = useStyles();
    const sideRef = useRef<HTMLDivElement>(null);
    const location = useLocation();
    const {
        mainLanding: {
            poker,
            sport,
            slots,
            liveDealer,
            dicechess,
            virtualSport,
            comingSoon,
            tournamentsTitle,
            cashBattleTitle,
            freePlayTitle,
            rulesTitle
        },
    } = useLocale();

    const { authStore, generalStore } = useStores();

    const [isOpen, setIsOpen] = useState(false);
    const canPlaySlots = authStore.currentUser?.canPlaySlots ?? false;

    useEffect(() => {
        const sideBarIsOpen =
            authStore.currentUser?.settings?.sideBarIsOpen ?? false;
        setIsOpen(sideBarIsOpen);
    }, [authStore.currentUser?.settings?.sideBarIsOpen]);

    const navList = useMemo(() => {
        if (canPlaySlots) {
            return [
                {
                    label: dicechess("Dicechess"),
                    icon: dicechessIcon,
                    active: location.pathname === paths.tournaments,
                    to: paths.tournaments,
                },
                {
                    label: poker("Poker"),
                    icon: pokerIcon,
                    active: location.pathname === paths.jackpoker,
                    to: paths.jackpoker,
                },
                {
                    label: sport("Sports"),
                    icon: sportIcon,
                    active: location.pathname === paths.sports,
                    to: paths.sports,
                },
                {
                    label: slots("Slots"),
                    icon: slotIcon,
                    active: location.pathname === paths.algaltente,
                    to: paths.algaltente,
                },
                {
                    label: liveDealer("Live Casino"),
                    icon: dealerIcon,
                    active: location.pathname === paths.liveDealer,
                    to: paths.liveDealer,
                },
                {
                    label: virtualSport("Virtual Sport"),
                    icon: virtualSportsIcon,
                    active: location.pathname === paths.virtualSports,
                    to: paths.virtualSports,
                },
            ];
        }

        return [
            {
                label: tournamentsTitle("Tournaments"),
                icon: tournamentIcon,
                active: location.pathname === paths.tournaments,
                to: paths.tournaments,
            },
            {
                label: cashBattleTitle("Cash Battle"),
                icon: cashBattleicon,
                active: location.pathname === paths.diceChessLobby,
                to: paths.diceChessLobby,
            },
            {
                label: freePlayTitle("Free Play"),
                icon: freePlayIcon,
                active: location.pathname === paths.lobbyFreePlay,
                to: paths.lobbyFreePlay,
            },
            {
                label: rulesTitle("Rules"),
                icon: rulesIcon,
                active: location.pathname === paths.gamesRules,
                to: paths.gamesRules,
            },
        ];
    }, [
        canPlaySlots,
        location.pathname,
        dicechess,
        poker,
        sport,
        slots,
        liveDealer,
        virtualSport,
    ]);

    const toggleMenu = async () => {
        const newIsOpen = !isOpen;
        setIsOpen(newIsOpen);
        await authStore.updateGameSettings({ sideBarIsOpen: newIsOpen });
    };

    const excludedPaths = useMemo(() => [
        paths.main,
        paths.login,
        paths.register,
        paths.lobby,
        paths.prePromo,
        paths.promo,
    ] as const, []);

    const shouldRenderSidebar = useMemo(() => {
        return !excludedPaths.includes(location.pathname as typeof excludedPaths[number]);
    }, [location.pathname, excludedPaths]);

    if (!shouldRenderSidebar) {
        return null;
    }

    return (
        <div
            ref={sideRef}
            className={cn(styles.containerSideBar, { open: isOpen })}
        >
            <ToggleIcon onClick={toggleMenu} open={isOpen} sideRef={sideRef} />
            {navList.map((item) => (
                <NavItem
                    key={item.to}
                    icon={item.icon}
                    label={item.label}
                    to={item.to}
                    active={item.active}
                    available={true}
                />
            ))}
        </div>
    );
});